import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import i18next from 'i18next';

/**
 * CalendarPicker — единый компонент выбора даты для портала TimeBerlo.
 * Дизайн — вариант «Компакт» (design.md, вариант 4). Тема (light/dark) и цвета
 * берутся из CSS-переменных сайта, все стили — в style.css (.cp-*).
 *
 * Режимы:
 *   mode="single" (по умолчанию) — одна дата.
 *   mode="range"                 — диапазон (две пилюли + один календарь).
 *
 * Значение может приходить/уходить как Date (по умолчанию) или как строка
 * 'YYYY-MM-DD' (output="string") — для замены нативных <input type="date">.
 */

type DateInput = Date | string | null | undefined;

interface CommonProps {
  minDate?: DateInput;
  maxDate?: DateInput;
  /** moment-формат для отображения в триггере. По умолчанию 'DD MMM YYYY'. */
  displayFormat?: string;
  /** 1 = неделя с понедельника (по умолчанию), 0 = с воскресенья. */
  weekStartsOn?: 0 | 1;
  /**
   * Вид триггера:
   *  'pill'  — компактная пилюля (тулбары, Reports) — по умолчанию;
   *  'field' — полноширинное поле-инпут (собственная рамка компонента);
   *  'bare'  — без своей рамки: оформление берётся из className формы (fx-input и т.п.).
   */
  variant?: 'pill' | 'field' | 'bare';
  /** Доп. классы на триггер. */
  className?: string;
  triggerStyle?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  /** Показывать ✕ для очистки значения. */
  clearable?: boolean;
}

interface SingleProps extends CommonProps {
  mode?: 'single';
  value: DateInput;
  onChange: (value: any) => void;
  /** 'date' → отдаёт Date|null; 'string' → отдаёт 'YYYY-MM-DD'|''. */
  output?: 'date' | 'string';
  stringFormat?: string;
}

interface RangeProps extends CommonProps {
  mode: 'range';
  startDate: DateInput;
  endDate: DateInput;
  onChange: (start: Date | null, end: Date | null) => void;
  separator?: React.ReactNode;
}

type Props = SingleProps | RangeProps;

// Названия месяцев/дней недели берутся из активного языка i18next (Intl) — без словарей.
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const activeLocale = (): string => {
  const l = (i18next.language || 'en').slice(0, 2);
  return l === 'uk' ? 'uk' : l === 'de' ? 'de' : 'en-GB';
};
const monthName = (locale: string, i: number, long: boolean) =>
  cap(new Intl.DateTimeFormat(locale, { month: long ? 'long' : 'short' }).format(new Date(2020, i, 1)));
const weekdayLabels = (locale: string, weekStart: 0 | 1): string[] => {
  const f = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const arr: string[] = [];
  for (let i = 0; i < 7; i++) arr.push(cap(f.format(new Date(2021, 1, 1 + i)))); // 2021-02-01 — понедельник
  return weekStart === 1 ? arr : [arr[6], ...arr.slice(0, 6)];
};

const toDate = (v: DateInput): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const m = moment(v, ['YYYY-MM-DD', moment.ISO_8601], true);
  const mm = m.isValid() ? m : moment(v);
  return mm.isValid() ? mm.toDate() : null;
};
const keyOf = (d: Date | null): number => (d ? d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate() : 0);
const sameDay = (a: Date | null, b: Date | null) => !!a && !!b && keyOf(a) === keyOf(b);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

function buildMatrix(view: Date, weekStart: 0 | 1): Date[] {
  const y = view.getFullYear();
  const m = view.getMonth();
  const first = new Date(y, m, 1);
  const lead = weekStart === 1 ? (first.getDay() + 6) % 7 : first.getDay();
  const start = new Date(y, m, 1 - lead);
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const rows = lead + daysInMonth > 35 ? 6 : 5;
  const cells: Date[] = [];
  for (let i = 0; i < rows * 7; i++) cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  return cells;
}

const CalendarPicker: React.FC<Props> = (props) => {
  const {
    minDate, maxDate, displayFormat = 'DD MMM YYYY', weekStartsOn = 1,
    variant = 'pill', className = '', triggerStyle, placeholder = '—', disabled = false,
  } = props;

  const isRange = props.mode === 'range';
  const min = toDate(minDate);
  const max = toDate(maxDate);

  const propStart = isRange ? toDate((props as RangeProps).startDate) : toDate((props as SingleProps).value);
  const propEnd = isRange ? toDate((props as RangeProps).endDate) : null;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<Date>(startOfMonth(propStart ?? new Date()));
  // черновик диапазона — коммитим наверх только когда выбраны обе даты
  const [draftStart, setDraftStart] = useState<Date | null>(propStart);
  const [draftEnd, setDraftEnd] = useState<Date | null>(propEnd);
  const [hover, setHover] = useState<Date | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const openPopup = useCallback(() => {
    if (disabled) return;
    setView(startOfMonth(propStart ?? new Date()));
    setDraftStart(propStart);
    setDraftEnd(propEnd);
    setHover(null);
    setOpen(true);
  }, [disabled, propStart, propEnd]);

  const place = useCallback(() => {
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const popH = popRef.current?.offsetHeight ?? 300;
    const popW = popRef.current?.offsetWidth ?? 250;
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow < popH + 10 && r.top > popH ? r.top - popH - 6 : r.bottom + 6;
    let left = r.left;
    if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
    setCoords({ top, left: Math.max(8, left) });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => place();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, place]);

  const isDisabled = (d: Date): boolean =>
    !!((min && keyOf(d) < keyOf(min)) || (max && keyOf(d) > keyOf(max)));

  const emitSingle = (d: Date | null) => {
    const p = props as SingleProps;
    if (p.output === 'string') p.onChange(d ? moment(d).format(p.stringFormat || 'YYYY-MM-DD') : '');
    else p.onChange(d);
  };

  const onPick = (d: Date) => {
    if (isDisabled(d)) return;
    if (!isRange) { emitSingle(d); setOpen(false); return; }
    // range
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(d); setDraftEnd(null);
    } else {
      if (keyOf(d) < keyOf(draftStart)) { setDraftStart(d); setDraftEnd(null); }
      else {
        setDraftEnd(d);
        (props as RangeProps).onChange(draftStart, d);
        setOpen(false);
      }
    }
  };

  // отображение
  const dispStart = open && isRange ? draftStart : propStart;
  const dispEnd = open && isRange ? (draftEnd ?? (draftStart && !draftEnd ? null : propEnd)) : propEnd;
  const fmt = (d: Date | null) => (d ? moment(d).format(displayFormat) : placeholder);

  const fmtRange = (s: Date | null, e: Date | null): React.ReactNode => {
    if (!s && !e) return placeholder;
    if (!s) return <>{placeholder} — {fmt(e)}</>;
    if (!e) return <>{fmt(s)} — {placeholder}</>;
    const sM = moment(s), eM = moment(e);
    const sameYear = sM.year() === eM.year();
    const sameMonth = sameYear && sM.month() === eM.month();
    if (sameMonth) return <><span>{sM.format('DD')}–{eM.format('DD MMM')}</span><span className="cp-range-yr">{eM.format('YYYY')}</span></>;
    if (sameYear) return <><span>{sM.format('DD MMM')}</span><span className="cp-range-dash">–</span><span>{eM.format('DD MMM')}</span><span className="cp-range-yr">{eM.format('YYYY')}</span></>;
    return <><span>{fmt(s)}</span><span className="cp-range-dash">–</span><span>{fmt(e)}</span></>;
  };

  // подсветка в сетке
  let hlStart = isRange ? draftStart : propStart;
  let hlEnd = isRange ? draftEnd : null;
  if (isRange && hlStart && !hlEnd && hover && keyOf(hover) >= keyOf(hlStart)) hlEnd = hover;

  const locale = activeLocale();
  const dows = weekdayLabels(locale, weekStartsOn);
  const cells = buildMatrix(view, weekStartsOn);

  // диапазон лет для select (учитывает min/max и текущий просмотр)
  const thisYear = new Date().getFullYear();
  const loY = Math.min(min ? min.getFullYear() : thisYear - 100, view.getFullYear());
  const hiY = Math.max(max ? max.getFullYear() : thisYear + 10, view.getFullYear());
  const yearOptions: number[] = [];
  for (let y = hiY; y >= loY; y--) yearOptions.push(y);

  const variantCls = variant === 'field' ? 'cp-field' : variant === 'bare' ? 'cp-bare' : 'cp-pill';
  const triggerCls = `cp-trigger ${variantCls} ${open ? 'cp-open' : ''} ${disabled ? 'cp-disabled-trigger' : ''} ${className}`;

  const popup = open ? createPortal(
    <div
      ref={popRef}
      className="cp-cal"
      data-portal-modal
      style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 'var(--z-portal)' as any }}
      onMouseLeave={() => setHover(null)}
    >
      <div className="cp-cal-top">
        <button type="button" className="cp-nav" onClick={() => setView(addMonths(view, -1))} aria-label="Prev">‹</button>
        <div className="cp-sels">
          <select className="cp-sel" value={view.getMonth()}
            onChange={(e) => setView(new Date(view.getFullYear(), +e.target.value, 1))}>
            {Array.from({ length: 12 }, (_, i) => i).map((i) => (
              <option key={i} value={i}>{monthName(locale, i, true)}</option>
            ))}
          </select>
          <select className="cp-sel" value={view.getFullYear()}
            onChange={(e) => setView(new Date(+e.target.value, view.getMonth(), 1))}>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button type="button" className="cp-nav" onClick={() => setView(addMonths(view, 1))} aria-label="Next">›</button>
      </div>

      <div className="cp-dow">{dows.map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="cp-grid">
        {cells.map((d, i) => {
          const col = i % 7;
          const other = d.getMonth() !== view.getMonth();
          const dis = isDisabled(d);
          const cls = ['cp-day'];
          if (other) cls.push('cp-other');
          if (dis) cls.push('cp-disabled');
          if (sameDay(d, new Date())) cls.push('cp-today');
          if (isRange) {
            const s = hlStart, e = hlEnd;
            if (s && e) {
              const kd = keyOf(d);
              if (kd === keyOf(s)) cls.push('cp-rs');
              if (kd === keyOf(e)) cls.push('cp-re');
              if (kd > keyOf(s) && kd < keyOf(e)) cls.push('cp-inr');
            } else if (s && sameDay(d, s)) cls.push('cp-rs', 'cp-re', 'cp-sel');
            if (cls.includes('cp-inr') || cls.includes('cp-rs') || cls.includes('cp-re')) {
              if (col === 0) cls.push('cp-edgel');
              if (col === 6) cls.push('cp-edger');
            }
          } else if (sameDay(d, propStart)) {
            cls.push('cp-sel');
          }
          return (
            <button
              type="button"
              key={i}
              className={cls.join(' ')}
              disabled={dis}
              onMouseEnter={() => isRange && setHover(d)}
              onClick={() => onPick(d)}
            >
              <span className="cp-dn">{d.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  ) : null;

  const sep = isRange ? ((props as RangeProps).separator ?? '→') : null;

  return (
    <>
      <div ref={anchorRef} className="cp-wrap" style={triggerStyle}>
        {isRange ? (
          <div className={`${triggerCls} cp-range-chip`} onClick={openPopup} role="button" tabIndex={0}>
            <span className="cp-ic">📅</span>
            <span className="cp-range-val">{fmtRange(dispStart, dispEnd)}</span>
          </div>
        ) : (
          <div className={triggerCls} onClick={openPopup} role="button" tabIndex={0}>
            <span className="cp-ic">📅</span><span className="cp-val">{fmt(propStart)}</span>
          </div>
        )}
      </div>
      {popup}
    </>
  );
};

export default CalendarPicker;
